import { findEntityAt } from './grid';
import { machinePorts } from './routing';
import type { EvalStep, SimState } from './state';
import { translate } from './types';
import type { Direction, GridPos } from './types';

function outputHeading(state: SimState, ref: EvalStep): { pos: GridPos; dir: Direction } | null {
  switch (ref.kind) {
    case 'observable': {
      const e = state.observables[ref.id];
      const { outSide } = machinePorts(state, e.position);
      return outSide ? { pos: e.position, dir: outSide } : null;
    }
    case 'conveyor': {
      const e = state.conveyors[ref.id];
      return { pos: e.position, dir: e.direction };
    }
    case 'machine': {
      const e = state.machines[ref.id];
      const { outSide } = machinePorts(state, e.position);
      return outSide ? { pos: e.position, dir: outSide } : null;
    }
    case 'sink':
      return null;
  }
}

/**
 * A Subject terminates the walk alongside an observable: its signals travel by name, never along the
 * output edges this order is built from, so it is not something the tick loop steps.
 */
function downstreamOf(state: SimState, ref: EvalStep): EvalStep | undefined {
  const heading = outputHeading(state, ref);
  if (!heading) return undefined;
  const target = findEntityAt(state, translate(heading.pos, heading.dir));
  if (!target || target.kind === 'observable' || target.kind === 'subject') return undefined;
  return { kind: target.kind, id: target.id };
}

function allRefs(state: SimState): EvalStep[] {
  const byId = (a: EvalStep, b: EvalStep) => a.id - b.id;
  return [
    ...Object.values(state.sinks).map((e): EvalStep => ({ kind: 'sink', id: e.id })),
    ...Object.values(state.machines).map((e): EvalStep => ({ kind: 'machine', id: e.id })),
    ...Object.values(state.conveyors).map((e): EvalStep => ({ kind: 'conveyor', id: e.id })),
    ...Object.values(state.observables).map((e): EvalStep => ({ kind: 'observable', id: e.id })),
  ].sort(byId);
}

/**
 * Downstream-first evaluation order (sinks before the entities feeding them), so capacity
 * freed by a consumer this tick is immediately visible upstream. Depth-first post-order
 * along each entity's output edge; cycles are broken at the revisit point, which simply
 * costs the cycle one tick of latency instead of special handling.
 */
export function computeEvalOrder(state: SimState): EvalStep[] {
  const order: EvalStep[] = [];
  const visited = new Set<string>();
  const inStack = new Set<string>();
  const keyOf = (ref: EvalStep) => `${ref.kind}:${ref.id}`;

  function visit(ref: EvalStep): void {
    const key = keyOf(ref);
    if (visited.has(key) || inStack.has(key)) return;
    inStack.add(key);
    const downstream = downstreamOf(state, ref);
    if (downstream) visit(downstream);
    inStack.delete(key);
    visited.add(key);
    order.push(ref);
  }

  for (const ref of allRefs(state)) visit(ref);
  return order;
}
